import React, { useCallback, useEffect, useState } from 'react';
import SuperAdminHeader from '../SuperAdminHeader';
import SuperAdminNavigation from '../SuperAdminNavigation';
import CommandPalette from './CommandPalette';
import SubTabs from '../ui/SubTabs';
import PageHeader from '../ui/PageHeader';
import ViewErrorBoundary from './ViewErrorBoundary';
import type { NavSection, NavTab } from '../../config/navigation';

interface AdminData {
  id: string;
  username: string;
  email: string;
  role: string;
}

interface AppShellProps {
  adminData: AdminData | null;
  onLogout: () => void;
  section: NavSection;
  tab: NavTab;
  onNavigate: (sectionKey: string, tabKey: string) => void;
  children: React.ReactNode;
}

const SIDEBAR_STORAGE_KEY = 'wns.admin.sidebarCollapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * The console's frame: persistent sidebar, sticky top bar, one scrolling
 * content column on a 12-column grid.
 *
 * Everything chrome-related is hoisted here so section components only ever
 * render their own content — previously each of the five sections re-rendered
 * its own title block, and one of them its own third row of tabs.
 */
const AppShell: React.FC<AppShellProps> = ({ adminData, onLogout, section, tab, onNavigate, children }) => {
  const [isCollapsed, setIsCollapsed] = useState(readCollapsed);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const toggleSidebar = useCallback(() => {
    setIsCollapsed((collapsed) => {
      const next = !collapsed;
      try {
        window.localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // Remembering the rail width is a nicety, not a requirement.
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="min-h-screen bg-surface-sunken text-ink">
      {/* Keyboard users previously had to tab through all 30 navigation
          controls to reach the content on every single view. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:rounded-lg focus:bg-brand-blue focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
      >
        Skip to content
      </a>

      <SuperAdminNavigation
        activeSection={section.key}
        activeTab={tab.key}
        onNavigate={onNavigate}
        isCollapsed={isCollapsed}
        isMobileOpen={isMobileNavOpen}
        onMobileClose={() => setIsMobileNavOpen(false)}
      />

      <div className={`transition-[padding] duration-200 ${isCollapsed ? 'lg:pl-sidebar-collapsed' : 'lg:pl-sidebar'}`}>
        <SuperAdminHeader
          adminData={adminData}
          onLogout={onLogout}
          section={section}
          tab={tab}
          onOpenSearch={() => setIsSearchOpen(true)}
          onToggleSidebar={toggleSidebar}
          onOpenMobileNav={() => setIsMobileNavOpen(true)}
          isSidebarCollapsed={isCollapsed}
        />

        <main id="main-content" tabIndex={-1} className="mx-auto max-w-[90rem] px-4 pb-16 pt-5 sm:px-6 lg:px-8">
          {/* One page header for the whole console. Each of the five sections
              used to render its own centred title block, and four of them
              rendered a second, different one per tab — fifteen copies of the
              same three-line component, none of them in agreement. */}
          <PageHeader title={section.label} description={tab.description} />

          {/* Sub-navigation sits with the content it switches, not stacked in
              the chrome — so the view's tabs are the last thing read before
              the view itself. */}
          <div className="mb-5 mt-4">
            <SubTabs
              tabs={section.tabs}
              activeTab={tab.key}
              onChange={(tabKey) => onNavigate(section.key, tabKey)}
              label={`${section.label} views`}
            />
          </div>

          <ViewErrorBoundary resetKey={`${section.key}/${tab.key}`}>{children}</ViewErrorBoundary>
        </main>
      </div>

      <CommandPalette
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onNavigate={onNavigate}
      />
    </div>
  );
};

export default AppShell;
